import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { SearchDomainsState } from './types'
import punycode from 'punycode/'
import ITld from '@/types/tld.interface'
import { HYDRATE } from 'next-redux-wrapper'
import getTldByDomain from '@/utils/helpers/get-tld.helper'
import EFetchStatus from '@/utils/enums/fetch-status.enum'
import { fetchDomain } from './async-actions'

const initialState: SearchDomainsState = {
  searchDomainValue: '',
  checkedTlds: [],
  searchingDomains: [],
  searchProgress: '',
  searchingStatus: 'waiting',
  availableTldsToSearch: [],
  tlds: [],
}

const domainSlice = createSlice({
  name: 'domain',
  initialState,
  reducers: {
    setTlds: (state, action: PayloadAction<ITld[]>) => {
      state.tlds = action.payload
      state.availableTldsToSearch = action.payload
    },
    setSearchDomainValue: (state, action: PayloadAction<string>) => {
      let domain = action.payload

      if (domain) {
        domain = domain.trim().toLowerCase()

        const isCyrillic = /[а-яА-Я]/.test(domain)

        let availableTldsToSearch: ITld[] = []

        if (isCyrillic) {
          availableTldsToSearch = state.tlds.filter((tld) => tld.category === 'cyrillic')
        } else {
          availableTldsToSearch = state.tlds.filter((tld) => tld.category !== 'cyrillic')
        }

        state.availableTldsToSearch = availableTldsToSearch
        state.checkedTlds = state.checkedTlds.filter((tld) =>
          availableTldsToSearch.map((tld) => tld.domain_tld).includes(tld)
        )

        const { tld } = getTldByDomain(state.tlds, domain)

        if (tld) {
          state.checkedTlds = [tld.domain_tld]
          state.availableTldsToSearch = state.tlds.filter((i) => i.domain_tld === tld.domain_tld)
        }
      }

      state.searchDomainValue = domain
    },
    toggleCheckedTld: (state, action: PayloadAction<string>) => {
      if (state.checkedTlds.includes(action.payload)) {
        state.checkedTlds = state.checkedTlds.filter((tld) => tld !== action.payload)
      } else {
        state.checkedTlds = [...state.checkedTlds, action.payload]
      }
    },
    prepareDomainsToSearch: (state) => {
      if (state.checkedTlds.length === 0) {
        state.checkedTlds = state.availableTldsToSearch.map((tld) => tld.domain_tld)
      }

      const { name } = getTldByDomain(state.tlds, state.searchDomainValue)

      state.searchingDomains = state.availableTldsToSearch
        .filter((tld) => state.checkedTlds.includes(tld.domain_tld))
        .map((tld) => ({
          domain: `${punycode.toASCII(name)}.${tld.domain_tld}`,
          status: EFetchStatus.WAITING,
          price: tld.registration_cost,
        }))

      if (state.searchingDomains.length === 0) return

      state.searchingStatus = 'loading'
      state.searchProgress = `0/${state.searchingDomains.length}`
    },
    selectAllTlds: (state) => {
      state.checkedTlds = state.availableTldsToSearch.map((tld) => tld.domain_tld)
    },
    clearSearchingDomains: (state) => {
      state.searchingDomains = []
      state.searchingStatus = 'waiting'
      state.searchProgress = ''
    },
  },
  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state, action) => {
      return {
        ...state,
        // @ts-ignore
        tlds: action.payload.domain.tlds,
        // @ts-ignore
        availableTldsToSearch: action.payload.domain.tlds,
      }
    }),
      builder.addCase(fetchDomain.pending, (state, action) => {
        const domainIndex = state.searchingDomains.findIndex(
          (domain) => domain.domain === action.meta.arg.domainName
        )
        if (domainIndex === -1) return
        state.searchingDomains[domainIndex].status = EFetchStatus.LOADING
        state.searchProgress = `${domainIndex + 1}/${state.searchingDomains.length}`
      }),
      builder.addCase(fetchDomain.fulfilled, (state, action) => {
        const domainIndex = state.searchingDomains.findIndex(
          (domain) => domain.domain === action.meta.arg.domainName
        )
        if (domainIndex === -1) return
        state.searchingDomains[domainIndex].status = EFetchStatus.SUCCESS
        state.searchProgress = `${domainIndex + 1}/${state.searchingDomains.length}`
        state.searchingDomains[domainIndex].response = action.payload

        if (!state.searchingDomains.find((domain) => domain.status === EFetchStatus.WAITING)) {
          state.searchingStatus = 'success'
        }
      }),
      builder.addCase(fetchDomain.rejected, (state, action) => {
        const domainIndex = state.searchingDomains.findIndex(
          (domain) => domain.domain === action.meta.arg.domainName
        )
        if (domainIndex === -1) return
        state.searchingDomains[domainIndex].status = EFetchStatus.ERROR
        state.searchProgress = `${domainIndex + 1}/${state.searchingDomains.length}`
        state.searchingDomains[domainIndex].errorResponse = action.error.message
      })
  },
})

export const {
  setTlds,
  prepareDomainsToSearch,
  setSearchDomainValue,
  toggleCheckedTld,
  selectAllTlds,
  clearSearchingDomains,
} = domainSlice.actions

export default domainSlice
